export type HttpError = Error & { status?: number; data?: unknown };

function buildUrl(
  input: string | URL,
  query?: Record<string, string | number | boolean | undefined>,
) {
  const url = new URL(
    String(input),
    typeof window !== "undefined" ? window.location.origin : "http://localhost",
  );
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined) continue;
      url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

async function parseJsonSafe(res: Response) {
  const text = await res.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export async function fetchJSON<T = unknown>(
  input: RequestInfo | URL,
  init?: RequestInit & {
    query?: Record<string, string | number | boolean | undefined>;
    parseAs?: "json" | "text" | "blob";
  },
): Promise<T> {
  const { query, parseAs = "json", headers, ...rest } = init || {};
  const url = typeof input === "string" || input instanceof URL ? buildUrl(input, query) : input;

  const res = await fetch(url as any, {
    ...rest,
    headers,
  });

  if (!res.ok) {
    const data = await parseJsonSafe(res);
    const err: HttpError = new Error(typeof data === "string" ? data : res.statusText);
    err.status = res.status;
    err.data = data;
    throw err;
  }

  if (parseAs === "text") return (await res.text()) as unknown as T;
  if (parseAs === "blob") return (await res.blob()) as unknown as T;
  // default json
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export async function getJSON<T = unknown>(
  url: string | URL,
  options?: Omit<Parameters<typeof fetchJSON<T>>[1], "parseAs">,
) {
  return fetchJSON<T>(url, { method: "GET", ...options });
}

export async function postJSON<T = unknown, B = unknown>(
  url: string | URL,
  body?: B,
  options?: Omit<Parameters<typeof fetchJSON<T>>[1], "parseAs">,
) {
  const extraHeaders = (options as any)?.headers as Record<string, string> | undefined;
  return fetchJSON<T>(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(extraHeaders || {}) },
    body: body !== undefined ? JSON.stringify(body) : undefined,
    ...options,
  });
}

export async function putJSON<T = unknown, B = unknown>(
  url: string | URL,
  body?: B,
  options?: Omit<Parameters<typeof fetchJSON<T>>[1], "parseAs">,
) {
  const extraHeaders = (options as any)?.headers as Record<string, string> | undefined;
  return fetchJSON<T>(url, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...(extraHeaders || {}) },
    body: body !== undefined ? JSON.stringify(body) : undefined,
    ...options,
  });
}

export async function delJSON<T = unknown>(
  url: string | URL,
  options?: Omit<Parameters<typeof fetchJSON<T>>[1], "parseAs">,
) {
  return fetchJSON<T>(url, { method: "DELETE", ...options });
}

// SWR-compatible fetcher signature retained for convenience
export async function fetcher<JSON = any>(input: RequestInfo, init?: RequestInit): Promise<JSON> {
  return fetchJSON<JSON>(input, init);
}
